/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState, useEffect, useCallback, forwardRef, useMemo } from 'react';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { selectSectionAndCategory, setActiveFilterButton } from '../../actions';

const SubLevelFilter = forwardRef(
    (
        {
            toplevel,
            filterType = '',
            filterHandle = '',
            filterDisplayName = '',
            handleSubLevelFilterPress = () => {},
            activeFilter = '',
            filterNamespace = '',
            filterKey = '',
        },
        ref
    ) => {
        const firstLetterUppercase = filterHandle?.charAt(0).toUpperCase();
        const remainingLetters = filterHandle.slice(1);
        const filterHandleMetafield = `${firstLetterUppercase}${remainingLetters}`;
        const filterClassName = classNames('sub-level__filter ', {
            active: activeFilter?.toLowerCase() === filterHandle?.toLowerCase(),
        });

        return (
            <button
                key={filterHandle}
                className={filterClassName}
                onClick={(e) => handleSubLevelFilterPress(e)}
                data-toplevel={toplevel}
                data-filtertype={filterType}
                data-filterhandle={filterType === 'collection' ? filterHandle : filterHandleMetafield}
                data-filternamespace={filterNamespace}
                data-filterkey={filterKey}
                ref={ref}
            >
                {filterDisplayName}
            </button>
        );
    }
);
SubLevelFilter.displayName = 'SubLevelFilter';

const ProductFilters = ({ dispatch, activeSection, activeFilter = null, skintone = '', filters }) => {
    const [currentTopLevelFilter, setCurrentTopLevelFilter] = useState(activeFilter?.filterTopLevel || null);
    const [topEmblaRef, topEmblaApi] = useEmblaCarousel({ align: 'start', dragFree: true });
    const [subEmblaRef, subEmblaApi] = useEmblaCarousel({ align: 'start', dragFree: true });
    const topFilterButtonsRef = useRef([]);
    const subFilterButtonsRef = useRef([]);
    const pendingAutoSelectFirstSubRef = useRef(false);
    const isTopLevelClickRef = useRef(false);

    const sectionFilters = useMemo(() => {
        if (activeSection === 'accessories') {
            return filters.filter((f) => f.section === 'accessories');
        } else {
            return filters.filter((f) => f.section === activeSection);
        }
    }, [filters, activeSection]);

    const scrollTopToButton = useCallback(
        (buttonEl) => {
            if (!buttonEl || !topEmblaApi) return;
            const idx = topFilterButtonsRef.current.findIndex((b) => b === buttonEl);
            if (idx > -1) topEmblaApi.scrollTo(idx);
        },
        [topEmblaApi]
    );

    const currentSubFilterHandles = useMemo(() => {
        if (!currentTopLevelFilter) return [];
        const parent = sectionFilters.find((f) => f.filterDisplayName === currentTopLevelFilter);
        if (!parent || !parent.subFilters) return [];
        return parent.subFilters
            .filter((sf) => sf.filterType && sf.filterHandle)
            .map((sf) =>
                sf.filterType === 'collection'
                    ? sf.filterHandle
                    : `${sf.filterHandle.charAt(0).toUpperCase()}${sf.filterHandle.slice(1)}`
            );
    }, [sectionFilters, currentTopLevelFilter]);

    const scrollSubToButton = useCallback(
        (handle) => {
            if (!handle || !subEmblaApi) return;
            const handleLc = handle?.toLowerCase();
            const idx = currentSubFilterHandles.findIndex((h) => h.toLowerCase() === handleLc);
            if (idx > -1) {
                subEmblaApi.scrollTo(idx, true);
                requestAnimationFrame(() => subEmblaApi.scrollTo(idx, true));
            }
        },
        [subEmblaApi, currentSubFilterHandles]
    );

    const scrollActiveSubFilter = useCallback(
        (af) => {
            if (!af?.filterHandle) return;
            scrollSubToButton(af.filterHandle);
        },
        [scrollSubToButton]
    );

    const fetchFilteredProducts = useCallback(
        (clickedFilter) => {
            if (!clickedFilter) return;
            if (clickedFilter.dataset.filtertype === 'collection') {
                dispatch(
                    selectSectionAndCategory(
                        {
                            id: activeSection,
                            title: '',
                            emptyText: '',
                            handle: clickedFilter.dataset.filterhandle,
                        },
                        null,
                        skintone
                    )
                );
            } else if (clickedFilter.dataset.filtertype === 'metafield') {
                dispatch(
                    selectSectionAndCategory(
                        {
                            id: activeSection,
                            title: '',
                            emptyText: '',
                            handle: 'all-legs',
                        },
                        {
                            key: clickedFilter.dataset.filterkey,
                            namespace: clickedFilter.dataset.filternamespace,
                            value: clickedFilter.dataset.filterhandle,
                        },
                        skintone
                    )
                );
            }

            dispatch(
                setActiveFilterButton({
                    filterType: clickedFilter.dataset.filtertype,
                    filterHandle: clickedFilter.dataset.filterhandle,
                    filterTopLevel: clickedFilter.dataset.toplevel,
                })
            );
        },
        [dispatch, activeSection, skintone]
    );

    // Re-init scroll position when slides change (filters array updates)
    useEffect(() => {
        if (activeFilter) {
            scrollActiveSubFilter(activeFilter);
        }
    }, [filters, activeFilter, scrollActiveSubFilter]);

    useEffect(() => {
        if (!currentTopLevelFilter) return;
        scrollActiveSubFilter(activeFilter);
    }, [currentTopLevelFilter, activeFilter, scrollActiveSubFilter]);

    useEffect(() => {
        if (!activeFilter || !topEmblaApi) return;
        const topIndex = topFilterButtonsRef.current.findIndex(
            (btn) => btn?.dataset?.toplevel === activeFilter.filterTopLevel
        );
        const suppress = isTopLevelClickRef.current;
        if (topIndex > -1 && !suppress) {
            topEmblaApi.scrollTo(topIndex, true);
            requestAnimationFrame(() => topEmblaApi.scrollTo(topIndex, true));
        }
        if (suppress) {
            isTopLevelClickRef.current = false;
        }
        scrollActiveSubFilter(activeFilter);
    }, [activeFilter, topEmblaApi, scrollActiveSubFilter]);

    useEffect(() => {
        scrollActiveSubFilter(activeFilter);
    }, [subEmblaApi, activeFilter, scrollActiveSubFilter]);

    const handleTopLevelFilterPress = (e) => {
        const clickedFilter = e.target;
        setCurrentTopLevelFilter(clickedFilter.dataset.toplevel);
        // Suppress automatic top-level scroll on the next activeFilter effect run! Feels quite over engineered, but it's been a pain in the arse... :)
        isTopLevelClickRef.current = true;

        topFilterButtonsRef.current.forEach((btn) => {
            if (!btn) return;
            if (btn.dataset.toplevel !== clickedFilter.dataset.toplevel) {
                btn.classList.remove('active');
            } else {
                btn.classList.add('active');
            }
        });

        scrollTopToButton(clickedFilter);

        if (clickedFilter.dataset.filtertype === 'collection') {
            console.log('fetching products', clickedFilter.dataset.filtertype);
            fetchFilteredProducts(clickedFilter);
        } else {
            console.log('pending auto select first sub filter', { clickedFilter }, clickedFilter.dataset.filtertype);
            pendingAutoSelectFirstSubRef.current = true;
        }
    };

    const handleSubLevelFilterPress = (e) => {
        const clickedFilter = e.target;
        fetchFilteredProducts(clickedFilter);
        const handle = clickedFilter?.dataset?.filterhandle;
        if (handle) {
            requestAnimationFrame(() => scrollSubToButton(handle));
        }
    };

    useEffect(() => {
        subFilterButtonsRef.current = [];
    }, [currentTopLevelFilter]);

    useEffect(() => {
        if (subEmblaApi) {
            try {
                subEmblaApi.reInit();
            } catch (_) {
                /* noop */
            }
        }
    }, [currentTopLevelFilter, currentSubFilterHandles.length, subEmblaApi]);

    // If user clicked a top-level group (metafield grouping) auto-select the first available subfilter.
    useEffect(() => {
        if (!pendingAutoSelectFirstSubRef.current) return;
        if (!currentTopLevelFilter) return;
        const parent = sectionFilters.find((f) => f.filterDisplayName === currentTopLevelFilter);
        if (!parent || !parent.subFilters || parent.subFilters.length === 0) return;
        const firstValid = parent.subFilters.find((sf) => sf.filterType && sf.filterHandle);
        if (!firstValid) return;
        const dataset = {
            filtertype: firstValid.filterType,
            filterhandle:
                firstValid.filterType === 'collection'
                    ? firstValid.filterHandle
                    : `${firstValid.filterHandle.charAt(0).toUpperCase()}${firstValid.filterHandle.slice(1)}`,
            toplevel: currentTopLevelFilter,
            filternamespace: firstValid.filterNamespace,
            filterkey: firstValid.filterKey,
        };
        const domBtn = subFilterButtonsRef.current.find(
            (btn) => btn && btn.dataset.filterhandle === dataset.filterhandle
        );
        fetchFilteredProducts(domBtn || { dataset });
        if (domBtn) {
            requestAnimationFrame(() => scrollSubToButton(domBtn.dataset.filterhandle));
        }
        pendingAutoSelectFirstSubRef.current = false;
    }, [currentTopLevelFilter, sectionFilters, fetchFilteredProducts, scrollSubToButton]);

    if (!sectionFilters || sectionFilters.length === 0) {
        return <></>;
    }

    return (
        <div id="buy-bits-container">
            <div className="filters">
                {/* Top-level filters carousel */}
                <div className="top-level embla" ref={topEmblaRef}>
                    <div className="embla__container">
                        {sectionFilters.map((f, idx) => (
                            <div className="embla__slide" key={f.filterDisplayName}>
                                {f.subFilters ? (
                                    <button
                                        ref={(el) => (topFilterButtonsRef.current[idx] = el)}
                                        className={`filter ${
                                            currentTopLevelFilter === f.filterDisplayName ? 'active' : ''
                                        }`}
                                        onClick={(e) => handleTopLevelFilterPress(e)}
                                        data-toplevel={f.filterDisplayName}
                                    >
                                        {f.filterDisplayName}
                                    </button>
                                ) : (
                                    <button
                                        ref={(el) => (topFilterButtonsRef.current[idx] = el)}
                                        className={`filter ${
                                            currentTopLevelFilter === f.filterDisplayName ? 'active' : ''
                                        }`}
                                        onClick={(e) => handleTopLevelFilterPress(e)}
                                        data-toplevel={f.filterDisplayName}
                                        data-filtertype={f.filterType}
                                        data-filterhandle={f.filterHandle}
                                    >
                                        {f.filterDisplayName}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {currentTopLevelFilter && (
                    <div
                        className={`sub-level embla ${!currentTopLevelFilter ? 'no-scroll' : ''}`}
                        ref={subEmblaRef}
                        key={`sub-${currentTopLevelFilter}`}
                    >
                        <div className="embla__container">
                            {sectionFilters.map(
                                (f) =>
                                    f.subFilters &&
                                    f.subFilters.map((subFilter) => {
                                        if (
                                            currentTopLevelFilter === f.filterDisplayName &&
                                            subFilter.filterType &&
                                            subFilter.filterHandle
                                        ) {
                                            const existingLength = subFilterButtonsRef.current.length;
                                            return (
                                                <div key={subFilter.filterHandle} className="embla__slide">
                                                    <SubLevelFilter
                                                        ref={(el) => {
                                                            if (el) {
                                                                subFilterButtonsRef.current[existingLength] = el;
                                                            }
                                                        }}
                                                        handleSubLevelFilterPress={(e) => handleSubLevelFilterPress(e)}
                                                        toplevel={f.filterDisplayName}
                                                        filterType={subFilter.filterType}
                                                        filterHandle={subFilter.filterHandle}
                                                        filterDisplayName={subFilter.filterDisplayName}
                                                        filterNamespace={subFilter.filterNamespace}
                                                        filterKey={subFilter.filterKey}
                                                        activeFilter={activeFilter?.filterHandle}
                                                    />
                                                </div>
                                            );
                                        }
                                        return null;
                                    })
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        activeSection: state.navigation.selected,
        products: state.browser.products,
        activeFilter: state.activeFilter,
        skintone: state.builds[state.currentBuildIndex].skintone,
        filters: state.filters,
    };
};

export default connect(mapStateToProps)(ProductFilters);

SubLevelFilter.propTypes = {
    toplevel: PropTypes.string.isRequired,
    filterType: PropTypes.string,
    filterHandle: PropTypes.string,
    filterDisplayName: PropTypes.string,
    handleSubLevelFilterPress: PropTypes.func,
    activeFilter: PropTypes.string,
    filterNamespace: PropTypes.string,
    filterKey: PropTypes.string,
};

ProductFilters.propTypes = {
    dispatch: PropTypes.func.isRequired,
    activeSection: PropTypes.string.isRequired,
    activeFilter: PropTypes.object,
    skintone: PropTypes.string,
    filters: PropTypes.arrayOf(PropTypes.object).isRequired,
};
